import { Question } from '@/modules/vendors/types';
import { cn } from '@/shared/utils/classname-merger';
import { Checkbox, Radio, RadioGroup, Select, SelectItem, Textarea } from '@nextui-org/react';
import React, { useCallback } from 'react';

interface QuestionFieldProps {
  question: Question;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  error?: string;
  // For checkboxes only - enable immediate API calls
  enableImmediateApi?: boolean;
  questionNumber?: number;
  onImmediateChange?: (questionNumber: number, value: string | string[]) => void;
  // For comments
  onCommentChange?: (comment: string) => void;
  // Optional override for display number (for merged lists)
  itemNumber?: number;
  // Disable all inputs when viewing from chat
  isDisabled?: boolean;
}

export const QuestionField: React.FC<QuestionFieldProps> = ({
  question,
  value,
  onChange,
  error,
  enableImmediateApi = false,
  questionNumber,
  onImmediateChange,
  onCommentChange,
  itemNumber,
  isDisabled = false
}) => {
  // Type guard to ensure value is safe to use
  const safeValue = (val: string | string[] | null | undefined): string | string[] => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return val;
    return question.question_type === 'multiple_choice' ? [] : '';
  };

  // Validate that selected values are from allowed choices
  const validateSelection = (newValue: string | string[]): boolean => {
    const allowedChoices = question.response_choices || [];

    if (Array.isArray(newValue)) {
      return newValue.every(choice => allowedChoices.includes(choice));
    }

    if (typeof newValue === 'string') {
      return allowedChoices.includes(newValue) || newValue === '';
    }

    return true; // Allow empty values
  };

  // Use safe value for rendering
  const currentValue = safeValue(value);

  // Individual checkbox change handler - call API immediately on every change for checkboxes
  const handleCheckboxChange = useCallback((choice: string, isSelected: boolean) => {
    // Get current values from props (not ref) to ensure we always have latest state
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = isSelected
      ? [...currentValues, choice]
      : currentValues.filter(v => v !== choice);

    // For checkboxes, call API immediately if enabled, otherwise use normal onChange
    if (enableImmediateApi && questionNumber && onImmediateChange) {
      onImmediateChange(questionNumber, newValues);
    } else {
      onChange(newValues);
    }
  }, [value, onChange, enableImmediateApi, questionNumber, onImmediateChange]);

  // Calculate the width needed for question number to ensure proper alignment
  const displayNumber = itemNumber ?? question.question_number;
  const questionNumberText = `${displayNumber}.`;
  // Approximate width: ~7px per character with slight padding
  const questionNumberWidth = Math.max(20, questionNumberText.length * 6 + 4);

  // Question header layout - Frame 6012
  const questionHeader = (
    <div className="flex flex-row items-start p-0 gap-2 w-full min-h-5 relative">
      {/* Question number - Frame 12 */}
      <div
        className="flex-shrink-0 flex items-start justify-start"
        style={{ minWidth: `${questionNumberWidth}px` }}
      >
        <span
          className="text-sm leading-5 font-normal text-[#3F3F46]"
          style={{ fontFamily: 'Inter' }}
        >
          {displayNumber}.
        </span>
      </div>

      {/* Question text */}
      <span
        className="flex-1 text-sm leading-5 font-normal text-[#3F3F46]"
        style={{ fontFamily: 'Inter' }}
      >
        {question.question}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </span>
    </div>
  );

  const renderField = () => {

    switch (question.question_type) {
      case 'free_text':
        return (
          <div className="flex flex-row items-center p-0 gap-2 w-full min-h-[44px]">
            {/* Empty space for alignment - Frame 12 */}
            <div className="flex-shrink-0" style={{ minWidth: `${questionNumberWidth}px` }} />

            {/* Response Field */}
            <Textarea
              placeholder={question.placeholder_text || "Add response"}
              value={typeof currentValue === 'string' ? currentValue : ''}
              onChange={(e) => onChange(e.target.value)}
              variant="flat"
              minRows={1}
              isDisabled={isDisabled}
              classNames={{
                base: "flex-1 min-h-[44px]",
                inputWrapper: "bg-[#FAFAFA] border border-[#E4E4E7] rounded-md p-3 min-h-[44px] hover:border-[#D4D4D8] focus-within:border-[#3F3F46] focus-within:ring-0",
                input: cn(
                  "text-sm leading-5 font-normal placeholder:text-[#A1A1AA] resize-none",
                  "focus:outline-none focus:ring-0",
                  currentValue ? "text-[#11181C]" : "text-[#A1A1AA]"
                )
              }}
              style={{
                fontFamily: 'Inter',
                boxSizing: 'border-box'
              }}
            />
          </div>
        );

      case 'single_choice':
        // Check if it's a dropdown (backend override) or small list (render as radio buttons) or large list (render as select)
        const choices = question.response_choices || [];
        const shouldRenderAsDropdown = question.is_dropdown || choices.length > 6;

        if (!shouldRenderAsDropdown) {
          return (
            <div className="flex flex-row items-start p-0 gap-2 w-full">
              {/* Empty space for alignment */}
              <div className="flex-shrink-0" style={{ minWidth: `${questionNumberWidth}px` }} />

              {/* Radio options container */}
              <div
                className="flex flex-col items-start py-2 gap-3 flex-1 rounded-lg"
                style={{
                  padding: '8px 0px',
                  filter: 'drop-shadow(0px 8px 24px rgba(0, 0, 0, 0.1))',
                  borderRadius: '8px'
                }}
              >
                <RadioGroup
                  value={typeof currentValue === 'string' ? currentValue : ''}
                  onValueChange={(selectedValue) => onChange(selectedValue)}
                  aria-label={`Select answer for question ${question.question_number}`}
                  isDisabled={isDisabled}
                >
                  {choices.map((choice, index) => (
                    <Radio
                      key={index}
                      value={choice}
                      classNames={{
                        base: "m-0",
                        label: "text-sm leading-5 font-medium text-[#11181C]"
                      }}
                      style={{ fontFamily: 'Inter' }}
                    >
                      {choice}
                    </Radio>
                  ))}
                </RadioGroup>
              </div>
            </div>
          );
        } else {
          return (
            <div className="flex flex-row items-center p-0 gap-2 w-full h-11">
              {/* Empty space for alignment */}
              <div className="flex-shrink-0" style={{ minWidth: `${questionNumberWidth}px` }} />

              {/* Select Field */}
              <div className="flex flex-row items-center p-3 gap-2 flex-1 h-11 bg-[#FAFAFA] border border-[#E4E4E7] rounded-md" style={{ boxSizing: 'border-box' }}>
                <Select
                  size="sm"
                  placeholder="Select an option"
                  isDisabled={isDisabled}
                  selectedKeys={typeof currentValue === 'string' && choices.includes(currentValue) ? [currentValue] : []}
                  onSelectionChange={(keys) => {
                    const selectedArray = Array.from(keys);
                    onChange(selectedArray[0] as string || '');
                  }}
                  classNames={{
                    mainWrapper: 'w-full',
                    trigger: 'flex items-center min-h-unit-6 h-8 border-0 shadow-none bg-transparent hover:bg-transparent data-[hover=true]:bg-transparent px-0 py-0',
                    value: 'flex items-center text-sm font-normal',
                    popoverContent: 'shadow-none border-1 border-gray-300',
                  }}
                >
                  {choices.map((choice) => (
                    <SelectItem key={choice} value={choice}>
                      {choice}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </div>
          );
        }

      case 'multiple_choice':
        const multiChoices = question.response_choices || [];
        const isSmallMultiList = multiChoices.length <= 8;

        if (isSmallMultiList) {
          return (
            <div className="flex flex-row items-start p-0 gap-2 w-full">
              {/* Empty space for alignment */}
              <div className="flex-shrink-0" style={{ minWidth: `${questionNumberWidth}px` }} />

              {/* Checkbox options container */}
              <div
                className="flex flex-col items-start py-2 gap-3 flex-1 rounded-lg"
                style={{
                  padding: '8px 0px',
                  borderRadius: '8px'
                }}
              >
                {/* Individual checkboxes - no group wrapper to ensure foolproof API calls */}
                <div className="flex flex-col gap-3">
                  {multiChoices.map((choice, index) => {
                    const isChecked = Array.isArray(currentValue) && currentValue.includes(choice);
                    return (
                      <Checkbox
                        key={index}
                        isSelected={isChecked}
                        isDisabled={isDisabled}
                        classNames={{
                          base: "m-0",
                          label: "text-sm leading-5 font-medium text-[#11181C]"
                        }}
                        style={{ fontFamily: 'Inter' }}
                        onChange={(e) => handleCheckboxChange(choice, e.target.checked)}
                      >
                        {choice}
                      </Checkbox>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="flex flex-row items-start p-0 gap-2 w-full h-11">
              {/* Empty space for alignment */}
              <div className="flex-shrink-0" style={{ minWidth: `${questionNumberWidth}px` }} />

              {/* Select Field for multi-select */}
              <div
                className="flex flex-row items-start p-3 gap-2 flex-1 h-11 bg-[#FAFAFA] border border-[#E4E4E7] rounded-md"
                style={{ boxSizing: 'border-box' }}
              >
                <Select
                  placeholder="Select"
                  selectionMode="multiple"
                  isDisabled={isDisabled}
                  selectedKeys={Array.isArray(currentValue) ? currentValue.filter(v => multiChoices.includes(v)) : []}
                  onSelectionChange={(keys) => {
                    const selectedArray = Array.from(keys) as string[];
                    onChange(selectedArray);
                  }}
                  variant="flat"
                  aria-label={`Select answers for question ${question.question_number}`}
                  classNames={{
                    base: "flex-1",
                    trigger: "shadow-none border-none bg-transparent p-0 min-h-0 h-5",
                    value: cn(
                      "text-sm leading-5 font-normal",
                      (Array.isArray(currentValue) && currentValue.length > 0) ? "text-[#11181C]" : "text-[#A1A1AA]"
                    ),
                    selectorIcon: "w-4 h-4 text-[#71717A]"
                  }}
                  style={{ fontFamily: 'Inter' }}
                >
                  {multiChoices.map((choice) => (
                    <SelectItem key={choice} value={choice}>
                      {choice}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </div>
          );
        }

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-start p-0 gap-1.5 w-full">
      {questionHeader}
      {renderField()}
      {error && (
        <div className="flex flex-row items-start p-0 gap-2 w-full">
          <div className="flex-shrink-0" style={{ minWidth: `${questionNumberWidth}px` }} />
          <span className="text-xs text-red-500 font-normal" style={{ fontFamily: 'Inter' }}>
            {error}
          </span>
        </div>
      )}
      {question.comments_allowed && onCommentChange && (
        <div className="flex flex-row items-start p-0 gap-2 w-full mt-2">
          {/* Empty space for alignment */}
          <div className="flex-shrink-0" style={{ minWidth: `${questionNumberWidth}px` }} />

          {/* Comments Field */}
          <Textarea
            placeholder="Add comments (optional)"
            value={question.comments || ''}
            onChange={(e) => onCommentChange(e.target.value)}
            variant="flat"
            minRows={2}
            isDisabled={isDisabled}
            classNames={{
              base: "flex-1",
              inputWrapper: "bg-[#FAFAFA] border border-[#E4E4E7] rounded-md p-3 hover:border-[#D4D4D8] focus-within:border-[#3F3F46] focus-within:ring-0",
              input: cn(
                "text-sm leading-5 font-normal placeholder:text-[#A1A1AA] resize-none",
                "focus:outline-none focus:ring-0",
                question.comments ? "text-[#11181C]" : "text-[#A1A1AA]"
              )
            }}
            style={{
              fontFamily: 'Inter',
              boxSizing: 'border-box'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default QuestionField;